import React, { useState, useEffect } from "react";
import "../../../css/CRUDModal.css";
import { apiList } from "../../../helpers/apiList";
import { axiosPostApi } from "../../../helpers/interceptorServices";
import HeaderWithBreadcrumb from "../../../components/common/HeaderWithBreadcrumb";
import MasterTable from "../../../components/common/MasterTable";
import Loader from "../../../components/Loader/Loader";
import GenerateStickerModal from "./GenerateStickerModal";
import GenerateStickerFilterModal from "./GenerateStickerFilterModal";
import { format, parse } from "date-fns";
import FilterBar from "../../../components/common/FilterBar";

// Statuses a sticker request goes through (stored as these strings)
const STICKER_STATUS_OPTIONS = [
  { value: "Pending Approval", label: "Pending Approval" },
  { value: "Approved", label: "Approved" },
  { value: "Pending", label: "Generating" },
  { value: "Generated", label: "Generated" },
  { value: "Rejected", label: "Rejected" },
  { value: "Cancelled", label: "Cancelled" },
];

// Fields the search box looks in (the list is filtered in the browser)
const SEARCH_FIELDS = ["requestedBy", "name", "mobile", "city", "barcodeSeries", "createdBy"];

const defaultFilterData = {
  requestedBy: "",
  mobile: "",
  search: "",
  status: "",
};

const GenerateSticker = () => {
  const [pageSize, setPageSize] = useState(10);
  const loggedInUserName = sessionStorage.getItem("username");
  const [loading, setLoading] = useState(true);
  const [noData, setNoData] = useState(false);
  const [stickerMasterData, setStickerMasterData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageDetails, setPageDetails] = useState(null);
  const [editData, setEditData] = useState({});
  const [modalAction, setModalAction] = useState("");
  const [columnData, setColumnData] = useState("");
  const [isModalOpenCreate, setIsModalOpenCreate] = useState(false);
  const [isModalOpenFilter, setIsModalOpenFilter] = useState(false);
  const [filterData, setFilterData] = useState(defaultFilterData);
  const [rawMasterData, setRawMasterData] = useState([]);

  // ✅ Modal open/close
  const openModalCreate = () => setIsModalOpenCreate(true);
  const closeModalCreate = () => setIsModalOpenCreate(false);

  const openModalFilter = () => setIsModalOpenFilter(true);
  const closeModalFilter = () => setIsModalOpenFilter(false);

  // ✅ Table headers
  const generateStickerHeader = [
    "Quantity",
    "City",
    "Requested by",
    "Mobile No",
    "Barcode series",
    "Status",
    "Approved by",
    "Remark",
    "Download",
  ];

  const generateStickerHeaderToKeyMap = {
    Quantity: "quantity",
    City: "city",
    "Requested by": "requestedBy",
    "Mobile No": "mobile",
    "Barcode series": "barcodeSeries",
    Status: "status",
    "Approved by": "approvedBy",
    Remark: "remarks",
    Download: "fileUrl",
  };

  const modifyStructure = (response) => {
    const modified = response?.map((user, index) => {
      const formattedCreDate = user?.createdDate
        ? parse(user.createdDate, "dd-MM-yyyy HH:mm:ss", new Date())
        : null;
      return {
        ...user,
        id: user.id || index + 1,
        createdDate: formattedCreDate
          ? [
              format(formattedCreDate, "dd-MM-yyyy"),
              <br key={index} />,
              format(formattedCreDate, "HH:mm:ss"),
            ]
          : user.createdDate || "",
      };
    });
    return modified;
  };

  // ✅ Fetch data (with dummy fallback & filtering)
  const fetchGenerateStickerData = async (
    page = currentPage,
    size = pageSize,
    currentFilters = filterData
  ) => {
    const hasFilters = Boolean(
      (currentFilters?.requestedBy &&
        currentFilters.requestedBy.trim() !== "") ||
        (currentFilters?.mobile && currentFilters.mobile.trim() !== "") ||
        (currentFilters?.search && currentFilters.search.trim() !== "") ||
        Boolean(currentFilters?.status)
    );

    const payload = {
      username: loggedInUserName,
      ...(currentFilters?.mobile && {
        mobile: currentFilters.mobile.trim(),
      }),
    };

    setLoading(true);

    try {
      const queryPageSize = hasFilters ? 1000 : size;
      const queryPageNo = hasFilters ? 1 : page;

      const response = await axiosPostApi(
        `${apiList.FETCH_GENERATE_STICKER}?&pageSize=${queryPageSize}&pageNo=${queryPageNo}`,
        payload
      );

      if (
        response?.data?.status === true &&
        response?.data?.respCode === "00"
      ) {
        let modifiedData = modifyStructure(response?.data?.data) || [];
        setRawMasterData(modifiedData);

        if (hasFilters) {
          if (
            currentFilters?.requestedBy &&
            currentFilters.requestedBy.trim() !== ""
          ) {
            const targetReq = currentFilters.requestedBy.trim().toLowerCase();
            modifiedData = modifiedData.filter(
              (item) =>
                (item?.requestedBy || "").trim().toLowerCase() === targetReq
            );
          }

          if (currentFilters?.mobile && currentFilters.mobile.trim() !== "") {
            const targetMob = currentFilters.mobile.trim().toLowerCase();
            modifiedData = modifiedData.filter(
              (item) =>
                String(item?.mobile || "")
                  .trim()
                  .toLowerCase() === targetMob
            );
          }

          if (currentFilters?.search && currentFilters.search.trim() !== "") {
            const term = currentFilters.search.trim().toLowerCase();
            modifiedData = modifiedData.filter((item) =>
              SEARCH_FIELDS.some((f) =>
                String(item?.[f] ?? "").toLowerCase().includes(term),
              ),
            );
          }

          if (currentFilters?.status) {
            const target = currentFilters.status.toLowerCase();
            modifiedData = modifiedData.filter(
              (item) => String(item?.status || "").toLowerCase() === target,
            );
          }

          const totalCount = modifiedData.length;
          const totalPages = Math.ceil(totalCount / size) || 1;
          const startIndex = (page - 1) * size;
          const paginatedData = modifiedData
            .slice(startIndex, startIndex + size)
            .map((item, idx) => ({
              ...item,
              id: startIndex + idx + 1,
            }));

          // same shape as the server's pageDetails (the table reads pageNo / noOfPages)
          setPageDetails({
            pageNo: page,
            currentPage: page,
            pageSize: size,
            totalRecords: totalCount,
            noOfPages: totalPages,
            totalPages: totalPages,
          });
          setStickerMasterData(paginatedData);
          setNoData(paginatedData.length === 0);
        } else {
          setPageDetails(response?.data?.pageDetails);
          setStickerMasterData(modifiedData);
          setNoData(modifiedData?.length === 0);
        }
      } else {
        setStickerMasterData([]);
        setNoData(true);
      }
    } catch (error) {
      console.warn("⚠️ API failed - showing dummy data for UI testing");
      setStickerMasterData([]);
      setNoData(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGenerateStickerData(1, pageSize, defaultFilterData);
  }, []);

  // ✅ Modal actions (add, modify, view)
  const handleChargeMaster = (data, actionType = "view") => {
    if (!["add", "modify", "view"].includes(actionType)) return;

    setModalAction(actionType);
    setColumnData(data);

    if (actionType === "add") {
      setEditData({});
      openModalCreate();
    } else {
      let selectedData = stickerMasterData.find(
        (item) => item?.id === data?.id
      );
      if (selectedData) {
        setEditData(selectedData);
        openModalCreate();
      }
    }
  };

  const applyFilters = (newFilters = filterData) => {
    setFilterData(newFilters);
    setCurrentPage(1);
    fetchGenerateStickerData(1, pageSize, newFilters);
  };

  const handleReset = () => {
    setFilterData(defaultFilterData);
    setCurrentPage(1);
    fetchGenerateStickerData(1, pageSize, defaultFilterData);
  };

  const totalRecord = pageDetails?.totalRecords || stickerMasterData.length;

  return (
    <>
      <HeaderWithBreadcrumb title="Generate Sticker" breadcrumb="Master" />
      <FilterBar
        values={filterData}
        onChange={applyFilters}
        search={{ key: "search", placeholder: "Search requested by, mobile, city or barcode series" }}
        selects={[{ key: "status", allLabel: "All statuses", chipLabel: "Status", options: STICKER_STATUS_OPTIONS }]}
      />
      <div>
        <div className="bg-[#FFFFFF] shadow-custom rounded-[14px] px-[10px] py-[10px]">
          {loading ? (
            <div className="flex justify-center items-center py-10">
              <Loader />
            </div>
          ) : (
            <MasterTable
              loading={loading}
              headers={generateStickerHeader}
              data={stickerMasterData}
              filterFields={["requestedBy", "mobile", "city", "barcodeSeries", "status"]}
              entityType="GenerateSticker"
              headerToKeyMap={generateStickerHeaderToKeyMap}
              handleAction={handleChargeMaster}
              noData={noData}
              pageDetails={pageDetails}
              currentPage={currentPage}
              pageSize={pageSize}
              totalRecord={totalRecord}
              fetchNextData={(page, size) => {
                setCurrentPage(page);
                setPageSize(size);
                fetchGenerateStickerData(page, size, filterData);
              }}
              setCurrentPage={setCurrentPage}
              setPageSize={setPageSize}
              openModalFilter={openModalFilter}
              handleReset={handleReset}
              filterData={filterData}
              fetchData={() => {}}
              hideFilterControls
            />
          )}

          {isModalOpenCreate && (
            <GenerateStickerModal
              show={isModalOpenCreate}
              handleClose={closeModalCreate}
              editData={editData}
              columnData={columnData}
              action={modalAction}
              callApi={() => fetchGenerateStickerData(1, pageSize, defaultFilterData)}
            />
          )}

          {isModalOpenFilter && (
            <GenerateStickerFilterModal
              show={isModalOpenFilter}
              handleClose={closeModalFilter}
              filterData={filterData}
              setFilterData={setFilterData}
              handleReset={handleReset}
              stickerList={rawMasterData}
              applyFilters={applyFilters}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default GenerateSticker;
